import { Block, Button, Checkbox, Flex, Icon, Typography } from '@aloft/toolkit-sui';
import Signature, { SignatureRef } from '@uiw/react-signature';

import { useRef, useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import z from 'zod';

import { CertifyModalForm } from '../schemas.ts';

export const CertifyModalSignature = ({
  form,
  hasAccountSignature,
  handleUseAccountSignature,
}: {
  form: UseFormReturn<z.input<CertifyModalForm>>;
  hasAccountSignature: boolean;
  handleUseAccountSignature: () => void;
}) => {
  const [showDrawCanvas, setShowDrawCanvas] = useState(false);
  const $svg = useRef<SignatureRef>(null);

  const signatureFile = form.watch('signatureFile');
  const stateSealFile = form.watch('stateSealFile');
  const useAccountSignature = form.watch('useAccountSignature');
  const saveSignature = form.watch('saveSignature');

  const handleOnSignatureFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (file) {
      form.setValue('signatureFile', file);
      form.setValue('useAccountSignature', false);
    }
  };

  const handleOnStateSealFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (file) {
      form.setValue('stateSealFile', file);
    }
  };

  const handleSignatureSave = () => {
    if (!$svg.current?.svg?.hasChildNodes()) {
      setShowDrawCanvas(false);
      return;
    }
    const svgelm = $svg.current?.svg?.cloneNode(true) as SVGSVGElement;
    const boundingBox = $svg.current?.svg?.getBBox({ stroke: true });
    const paddedBoundingBox = {
      x: (boundingBox?.x || 0) - 10,
      y: (boundingBox?.y || 0) - 10,
      width: (boundingBox?.width || 0) + 20,
      height: (boundingBox?.height || 0) + 20,
    };
    const clientWidth = $svg.current?.svg?.clientWidth;
    const clientHeight = $svg.current?.svg?.clientHeight;
    const svgWidth = Math.min(Math.round(paddedBoundingBox?.width || 0), clientWidth);
    const svgHeight = Math.min(Math.round(paddedBoundingBox?.height || 0), clientHeight);
    svgelm.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svgelm.removeAttribute('style');
    svgelm.setAttribute('width', `${svgWidth}px`);
    svgelm.setAttribute('height', `${svgHeight}px`);
    svgelm.setAttribute('viewBox', `${paddedBoundingBox.x} ${paddedBoundingBox.y} ${svgWidth} ${svgHeight}`);
    const data = new XMLSerializer().serializeToString(svgelm);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = svgWidth || 0;
      canvas.height = svgHeight || 0;
      ctx?.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'signature.png', { type: blob.type });
          form.setValue('signatureFile', file);
          form.setValue('useAccountSignature', false);
          setShowDrawCanvas(false);
        }
      });
    };
    img.src = `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(data)))}`;
  };

  return (
    <FormProvider {...form}>
      <Flex flexDirection="column">
        <Flex marginBottom="5px" gap={4}>
          <Flex justifyContent="space-between" alignItems="center" flex={1}>
            <Typography variant="h3" color="textSecondary">
              Signature
              <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                *
              </span>
            </Typography>
            <Block>
              {showDrawCanvas ? (
                <Flex gap={2}>
                  <Button
                    onClick={() => {
                      setShowDrawCanvas(false);
                    }}
                    icon="close"
                    text="Cancel"
                    variant="inline"
                    size="sm"
                  />
                  <Button
                    onClick={() => {
                      $svg.current?.clear();
                    }}
                    icon="delete-bin"
                    text="Clear"
                    variant="inline"
                    size="sm"
                  />
                  <Button
                    onClick={handleSignatureSave}
                    icon="check"
                    text="Save"
                    variant="inline"
                    size="sm"
                  />
                </Flex>
              ) : (
                <Flex gap={2}>
                  {hasAccountSignature && !useAccountSignature && (
                    <Button
                      icon="refresh"
                      text="Reuse previous signature"
                      variant="inline"
                      size="sm"
                      onClick={handleUseAccountSignature}
                    />
                  )}
                  <Flex alignItems="center">
                    <label htmlFor="signatureFileInput" style={{ cursor: 'pointer' }}>
                      <Flex alignItems="center" gap="8px">
                        <Icon icon="edit" size="sm" color="buttonDefault" />
                        <Typography variant="h5" color="buttonDefault">
                          Change
                        </Typography>
                      </Flex>
                      <input
                        id="signatureFileInput"
                        type="file"
                        accept="image/png"
                        style={{ display: 'none' }}
                        onChange={handleOnSignatureFileAdd}
                      />
                    </label>
                  </Flex>
                </Flex>
              )}
            </Block>
          </Flex>
          <Flex justifyContent="space-between" width="122px" alignItems="center">
            <Typography variant="h3" color="textSecondary">
              State Seal
            </Typography>
            <label htmlFor="stateSealFileInput" style={{ cursor: 'pointer' }}>
              <Flex alignItems="center" gap="8px">
                <Icon size="sm" icon="edit" color="buttonDefault" />
              </Flex>
              <input
                id="stateSealFileInput"
                type="file"
                accept="image/png"
                style={{ display: 'none' }}
                onChange={handleOnStateSealFileAdd}
              />
            </label>
          </Flex>
        </Flex>
        <Flex gap={4}>
          {showDrawCanvas ? (
            <Block
              flex={1}
              backgroundColor="surface2"
              borderWidth="1px"
              borderStyle="solid"
              borderColor="strokeDefault"
              position="relative"
              height="122px"
            >
              <Signature ref={$svg} />
            </Block>
          ) : (
            <Flex
              flex={1}
              backgroundColor="surface2"
              borderWidth="1px"
              borderRadius="5px"
              borderStyle="solid"
              borderColor="strokeDefault"
              paddingTop="10px"
              paddingBottom="20px"
              position="relative"
              height="122px"
              justifyContent="center"
            >
              {signatureFile && (
                <img
                  style={{ height: '90px', objectFit: 'contain' }}
                  src={URL.createObjectURL(signatureFile)}
                  alt="current signature"
                />
              )}

              <Flex position="absolute" right="5px" bottom="5px">
                <Button
                  icon="signature-draw"
                  size="sm"
                  onClick={() => {
                    setShowDrawCanvas(true);
                  }}
                  text="Draw a signature"
                  variant="inline"
                />
              </Flex>
            </Flex>
          )}

          <Block width="122px" borderWidth="1px" borderStyle="solid" borderColor="strokeLight">
            {stateSealFile && (
              <img
                style={{ height: '122px', width: '100%', objectFit: 'contain' }}
                src={URL.createObjectURL(stateSealFile)}
                alt="current state seal"
              />
            )}
          </Block>
        </Flex>
        <Block marginTop="5px">
          {!useAccountSignature && (
            <Checkbox
              isChecked={saveSignature}
              onChange={(e) => form.setValue('saveSignature', e.target.checked)}
            >
              <Typography variant="body1" color="textSecondary">
                Save this signature for future orders
              </Typography>
            </Checkbox>
          )}
        </Block>
        <Block marginTop="10px">
          <Typography variant="body1" color="textSecondary">
            After you sign this order, changes will only be possible through a Revision or Review of
            Value (ROV) request. You will still have access to the signed PDF for your records.
          </Typography>
        </Block>
      </Flex>
    </FormProvider>
  );
};
